import React, { useState, useEffect, useCallback, Suspense, FunctionComponent } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment-timezone";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Label,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { useAddAlert } from "src/store/actions";
import { useStoreContext } from "src/store/context";
import { additionalCharts, alertMessages, fourMain, mainQuery } from "src/constants";
import { getCharts, getNfdrMinMax, getPercentileLevels } from "src/apis/getNfdrsCalls";
import { truncateOneDecimal } from "src/store/common";
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import LoadingSpinner from 'src/shared/loading/loading';
import { useLocation } from "react-router-dom";
import { getWeatherMinMax } from "src/apis/getWeatherCalls";

type ChartsProps = {
  station: any;
};
type CProps = {
  data: any[];
  fuelModel: any;
  start: any;
  end:any;
  chartsToDisplay: any[];
  xTicks:any[];
  selectedYr: string;
  percentileLines: any;
  removeChart: (id: any)=>void;
  station: any;
};

const generateKey = () => {
  const charSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const crypto = globalThis.crypto;
  const values = new Uint8Array(32);
  crypto.getRandomValues(values);
  let key = '';
  for (let i = 0; i < 32; i++) {
    key += charSet[values[i] % charSet.length]
  }
  return key
}


const CustomizedAxisTick: FunctionComponent<any> = (props: any) => {
  const { x, y, payload } = props;
  return (
    <g transform={`translate(${x},${y})`} >
      {
        (
          <text
            fontSize=".75rem"
            x={0}
            y={2}
            dy={16}
            textAnchor="end"
            fill="#666"
            transform="rotate(-35)"
          >
            {moment(payload.value).format('MMM')}
          </text>
        )
      }
    </g>
  );
};

const CustomTooltip: any = (props: any) => {
  const { active, label, payload, config, percentileLines, getLines, diffYears } = props;
  if (active && payload?.length) {
    return (
      <div className="custom-tooltip" style={{ backgroundColor: 'white', border: 'none !important', display: 'block', margin: '3px', padding: '3px', width: '120px'}}>
        <span className="month" style={{ fontSize: '1rem', paddingBottom: '0px', paddingTop: '0px', display: 'block' }}>
          {moment(label).format('MM-DD-YY')}
        </span>
        { ((payload?.length>=3)) && config.id!=='gsi'&& diffYears>5 && payload.map((p: any) => {
            return (((payload.length===5) && p.name==='Forecast')? null:<svg key={`${p.dataKey}`} width="200" height="15" style={{ display: 'block', marginTop: '3px', paddingBottom: '0px' }}>
              <rect width="5" height="5" style={{ fill: `${p.color}` }} />
              <text
                fontSize=".9rem"
                height="5"
                x={10}
                y={10}
              >
                {p ? `${truncateOneDecimal(Number(p.payload[`${p.dataKey}`]))} (${ p.name})` : 'null'}
              </text>
            </svg>)
        })}
        {payload?.length>0 && config.id ==='gsi' && payload.map((p: any) => {
            return ((payload.length===3 && p.name==='Forecast')? null:<svg key={`${p.dataKey}`} width="200" height="15" style={{ display: 'block', marginTop: '3px', paddingBottom: '0px' }}>
              <rect width="5" height="4" style={{ fill: `${p.color}` }} />
              <text
                fontSize=".9rem"
                height="5"
                x={10}
                y={10}
              >
                {p ? `${truncateOneDecimal(Number(p.payload[`${p.dataKey}`]))} (${ p.name})` : 'null'}
              </text>
            </svg>)
        })
        }
        {payload?.length>0 && config.id !=='gsi' && diffYears <=5 && payload.map((p: any) => {
            return ((payload.length===3 && p.name==='Forecast')? null:<svg key={`${p.dataKey}`} width="200" height="15" style={{ display: 'block', marginTop: '3px', paddingBottom: '0px' }}>
              <rect width="5" height="4" style={{ fill: `${p.color}` }} />
              <text
                fontSize=".9rem"
                height="5"
                x={10}
                y={10}
              >
                {p ? `${truncateOneDecimal(Number(p.payload[`${p.dataKey}`]))} (${ p.name})` : 'null'}
              </text>
            </svg>)
        })
        }
        {
          config.id!=='gsi' && config.percLine1 && percentileLines && diffYears>5 &&(
            <svg key={`${config.labelPercLine1}%: ${getLines(`${config.id}${config.daily}`, config.percLine1)} ${generateKey()}`} width="200" height="15" style={{ display: 'block', marginTop: '3px', paddingBottom: '0px' }}>
              <text
                fontSize=".9rem"
                height="5"
                x={5}
                y={10}
              >
              {`${config.labelPercLine1}%: ${getLines(`${config.id}${config.daily}`, config.percLine1)}`}
            </text>
            </svg>
          )
        }
        {
          config.id!=='gsi' && config.percLine2 && percentileLines && diffYears>5 && (
            <svg key={`${config.labelPercLine1}%: ${getLines(`${config.id}${config.daily}`, config.percLine1)} ${generateKey()}`} width="200" height="15" style={{ display: 'block', marginTop: '3px', paddingBottom: '0px' }}>
              <text
              fontSize=".9rem"
              height="5"
              x={5}
              y={10}
            >
              {`${config.labelPercLine2}%: ${getLines(`${config.id}${config.daily}`, config.percLine2)}`}
            </text>
          </svg>
          )
        }

      </div>
    );
  }
  return null;
};

const Charts:React.FC<CProps> = ({
  chartsToDisplay, 
  data,
  fuelModel,
  start,
  end, 
  xTicks, 
  selectedYr, 
  percentileLines, 
  removeChart,
  station
})=>{
  const diffYears = Math.abs(moment(station?.period_record_start).diff(moment(station?.period_record_stop), 'years'))
  const getLines=(comp:string, lineVal: string)=>{
    if(percentileLines){
      return percentileLines[comp][lineVal]
    }
  };
  const currentYr = moment().format('YYYY');
  return(      
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(1, 1fr)",
      }}
    >
      {chartsToDisplay
      .flat().map((config) => config? (
        <Box key={config.title} sx={{ border: "1px solid #ddd", padding: 2, backgroundColor: "#f5f5f5", gridColumn: 'span 2' }}>
          {
            config.id !=='energy_release_component' && 
            config.id !=='spread_component' && 
            config.id !=='burning_index' && 
            config.id !=='ignition_component' ? 
            (
              <Grid container justifyContent="flex-end">
                <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#3d3d3d", textAlign: "left" }}>
                  <span style={{fontWeight: 700}}>{`${station?.station_name} - `}</span> {station?.time_zone_offset && ` (POR: ${moment(station?.period_record_start).utc().format('MM-DD-YYYY')} - 12-31-2024)`}
                </Typography>
                </Grid>
                <Grid item xs={1} position='absolute'>
                    <IconButton onClick={(e)=>removeChart(config.id)}>
                      <CloseIcon/>
                    </IconButton>
                </Grid>
              </Grid>

            ) :
                <Typography variant="body2" sx={{ color: "#3d3d3d", textAlign: "left" }}>
                  <span style={{fontWeight: 700}}>{`${station?.station_name} -`}</span> {` NFDRS Fuel Model ${fuelModel}`} {station?.time_zone_offset && ` (POR: ${moment(station?.period_record_start).utc().format('MM-DD-YYYY')} - 12-31-2024)`}
                </Typography>

          }

          <Typography variant="h6" sx={{ fontWeight: "bold", color: "black", textAlign: "left", marginTop: 1 }}>
            {config.title}
          </Typography>

          <ResponsiveContainer width="100%" height={500}>
            <LineChart
              data={data}
              margin={{ top: 10, right: 40, left: 5, bottom: 10 }}
            >
              <XAxis dataKey="date" scale='time' type='number' ticks={xTicks} tick={<CustomizedAxisTick />} interval={0} domain={[start, end]}>
                <Label offset={-2} position="insideBottom" />
              </XAxis>
              <YAxis type='number'>
                <Label
                  value={config.yAxisLabel}
                  angle={-90}
                  dx={-10}
                />
              </YAxis>
              <Legend iconType="plainline" dy={-10}/>
              {
                data.length>0 && config.id ==='gsi' && diffYears>5 &&
                <ReferenceLine
                y={0.3}
                stroke="gray"
                strokeWidth={1.5}
                label={{
                  value: `0.3`,
                  position: "right",
                  fill: "gray",
                  fontSize: 12,
                }}
              />
              }
              {
                data.length>0 && percentileLines!==null && config.percLine2!==''&&  diffYears>5 && config.id !=='gsi' &&
                <ReferenceLine
                y={Number(getLines(`${config.id}${config.dailyPerc}`, config.percLine2))}
                stroke="gray"
                strokeWidth={1.5}
                label={{
                  dy: -10,
                  value: `${config.labelPercLine2}%`,
                  position: "right",
                  fill: "gray",
                  fontSize: 12,
                }}
              />
              }   
              {
                data.length>0 && percentileLines!==null && config.percLine1!==''&& diffYears>5 && config.id !=='gsi' && 
              <ReferenceLine
                y={Number(getLines(`${config.id}${config.dailyPerc}`, config.percLine1))}
                stroke="gray"
                strokeWidth={1.5}
                label={{
                  dy:10,
                  value: `${config.labelPercLine1}%`,
                  position: "right",
                  fill: "gray",
                  fontSize: 12,
                }}
              />
              }     
              
              {
                data.length>0 && percentileLines!==null && config.percLine3!==''&& diffYears>5 && config.id !=='gsi' && 
              <ReferenceLine
                y={Number(getLines(`${config.id}${config.dailyPerc}`, config.percLine3))}
                stroke="gray"
                strokeWidth={1.5}
                label={{
                  dy:10,
                  value: `${config.labelPercLine3}%`,
                  position: "right",
                  fill: "gray",
                  fontSize: 12,
                }}
              />
              }   
              {
                data.length>0 && percentileLines!==null && config.percLine4!==''&& diffYears>5 && config.id !=='gsi' &&
              <ReferenceLine
                y={Number(getLines(`${config.id}${config.dailyPerc}`, config.percLine4))}
                stroke="gray"
                strokeWidth={1.5}
                label={{
                  dy:10,
                  value: `${config.labelPercLine4}%`,
                  position: "right",
                  fill: "gray",
                  fontSize: 12,
                }}
              />
              }   
              {
                data.length>0 && percentileLines!==null && config.percLine5!==''&& diffYears>5 && config.id !=='gsi' &&
              <ReferenceLine
                y={Number(getLines(`${config.id}${config.dailyPerc}`, config.percLine5))}
                stroke="gray"
                strokeWidth={1.5}
                label={{
                  dy:10,
                  value: `${config.labelPercLine5}%`,
                  position: "right",
                  fill: "gray",
                  fontSize: 12,
                }}
              />
              }   
              {
                data.length>0 && percentileLines!==null && config.percLine6!==''&& diffYears>5 && config.id !=='gsi' && 
              <ReferenceLine
                y={Number(getLines(`${config.id}${config.dailyPerc}`, config.percLine6))}
                stroke="gray"
                strokeWidth={1.5}
                label={{
                  dy:10,
                  value: `${config.labelPercLine6}%`,
                  position: "right",
                  fill: "gray",
                  fontSize: 12,
                }}
              />
              }   
              <Tooltip content={<CustomTooltip config={config} percentileLines={percentileLines} getLines={getLines} diffYears={diffYears}/>} position={{ y: -15 }} />
              {
                 (diffYears>5) &&config.id !=='gsi' &&
                <Line
                  type="monotone"
                  dataKey={`${config.id}_all_max`}
                  stroke={`${config.maxColor}`}
                  strokeWidth={1.5}
                  name="Max"
                  dot={false}
                  connectNulls={true}

                />
              }
              <Line
                type="monotone"
                dataKey={`${config.id}_all_avg`}
                stroke={`${config.avgColor}`}
                strokeWidth={1.5}
                name="Avg"
                dot={false}
                connectNulls={true}

              />
              {
               (diffYears>5) && config.id !=='gsi' &&
                <Line
                type="monotone"
                dataKey={`${config.id}_all_min`}
                stroke={`${config.minColor}`}
                strokeWidth={1.5}
                name="Min"
                dot={false}
                connectNulls={true}
              />
              }
              {/* Solid line for Observations  */}
              <Line
                type="monotone"
                dataKey={`${config.id}${config.daily}`}
                stroke="black"
                strokeWidth={3}
                name={`${selectedYr} (Obs)`}
                dot={false}
                connectNulls={true}
              />
              {/* Dotted line for Forecast */}
              {
                currentYr === selectedYr && 
                <Line
                  type="monotone"
                  dataKey={`${config.id}${config.daily}_f`}
                  stroke="black"
                  strokeWidth={3}
                  name={`Forecast`}
                  dot={false}
                  strokeDasharray="5 5"
                  connectNulls={true}
                />
              }
            </LineChart>
          </ResponsiveContainer>

        </Box>
      ): (null)
      )}
    </Box>
  )
}

export const ChartsTab: React.FC<ChartsProps> = ({ station }) => {

  const [state, dispatch] = useStoreContext();
  const [fuelModel, setFuelModel] = useState("Y");
  const [chartsSelected, setChartsSelected] = useState<any[]>([]);
  const [date, setDate] = useState<Date | null>(moment().set({ month: 0, date: 2 }).utc().toDate());
  const [errorDate, setErrorDate] = useState<string | null>(null);
  const addAlert = useAddAlert(dispatch);
  const [currStation, setCurrStation] = useState<any>(station)

  const [fourMainChartData, setFourMainChartData] = useState<any[]>([]);
  const [additionalChartObj, setAdditionalChartObj] =useState<any[]>([]);
  const [dataUsedForCharts, setDataUsedForCharts] = useState<any[]>([]);

  const [startOfChartX, setStartOfChartX]=useState<any>();
  const [endOfChartX, setEndOfChartX]=useState<any>();
  const [xTicks, setXTicks]= useState<any[]>([]);
  const [yrSelected, setYrSelected]=useState<string>('2025');
  const [percentileLines, setPercentileLines] = useState<any>(null);

  const [callsComplete, setCallsComplete] = useState<boolean>(false);
  const location = useLocation();

  const [nfdrsCurrentValues, setNfdrsCurrentValues ] = useState<any>([]);
  const [wxCurrentValues, setWxCurrentValues ] = useState<any>([]);

  const [nfdrsCurrFailCount, setNfdrsCurrFailCount] = useState<number>(0);
  const [wxCurrFailCount, setWxCurrFailCount] = useState<number>(0);
  const [percLineFailCount, setPercLineFailCount] = useState<number>(0)

  //formats for nfdrs ones
  const formatForChart = useCallback((currentData: any, dailies:any, yr?:string)=>{
    let yearSelected:string='';

    if(yr){
      yearSelected=yr
    }else{
      yearSelected= moment(date).format('YYYY');
    } 
    setYrSelected(yearSelected)
    const lastItemCurrent = currentData[currentData.length-1];
    const lastMD = moment(lastItemCurrent?.summary_date).utcOffset(currStation?.time_zone_offset, true).format('MM-DD')

    const firstItemCurrent = currentData[0];
    const firstMD = moment(firstItemCurrent?.summary_date).utcOffset(currStation?.time_zone_offset, true).format('MM-DD')
    /* need to combine current daily values with the daily from historic*/
    /* the chart takes the following:
      energy_release_component_max_f would be the current forecast line
      energy_release_component_max would be the current obs line
      energy_release_component_all_max is for all period of record line

    */
    /*below will find the index of the first instance of forecast for the current line (black line)*/
    const firstForecastForCurrentIndex = currentData.findIndex((currentDaily:any)=> currentDaily.nfdr_type==='F');
    /*this will get the object (hopefully obser type) to fill in the line between forecast and obs*/
    const theObservationBeforeForecastObj = currentData[firstForecastForCurrentIndex-1];
    
    const slicedAllRecordDaily = dailies.slice(dailies.findIndex((daily:any)=>daily.day === firstMD),dailies.findIndex((daily:any)=>daily.day === lastMD)+1)

    let gapFilling = slicedAllRecordDaily.map((allRecDaily: any, index: number, currentArray: any) => {

    const theCurrentDaily = currentData.find((currentDaily:any)=> moment(currentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD') === allRecDaily.day);
    if(theCurrentDaily?.nfdr_type==='O'){
        let forecastToConnectLines:any;
        /*below is to add a forecast object for the last observation object found to fill in the 
        gap on the chart between obs and forecast*/
        if(theCurrentDaily.summary_date===theObservationBeforeForecastObj?.summary_date){
          forecastToConnectLines={
            energy_release_component_max_f: theCurrentDaily.energy_release_component_max,
            burning_index_max_f: theCurrentDaily.burning_index_max,
            gsi_f: theCurrentDaily.gsi,
            herbaceous_lfi_fuel_moisture_f: theCurrentDaily.herbaceous_lfi_fuel_moisture,
            hun_hr_tl_fuel_moisture_min_f: theCurrentDaily.hun_hr_tl_fuel_moisture_min,
            ignition_component_max_f: theCurrentDaily.ignition_component_max,
            kbdi_f: theCurrentDaily.kbdi,
            one_hr_tl_fuel_moisture_min_f: theCurrentDaily.one_hr_tl_fuel_moisture_min,
            spread_component_max_f: theCurrentDaily.spread_component_max,
            station_id: theCurrentDaily.station_id,
            ten_hr_tl_fuel_moisture_min_f: theCurrentDaily.ten_hr_tl_fuel_moisture_min,
            thou_hr_tl_fuel_moisture_min_f: theCurrentDaily.thou_hr_tl_fuel_moisture_min,
            woody_lfi_fuel_moisture_f: theCurrentDaily.woody_lfi_fuel_moisture,
          }
        }
        if(theCurrentDaily){
          return({
            ...forecastToConnectLines,
            dateString:moment(theCurrentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
            date: Number(moment(theCurrentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
            ...theCurrentDaily,
            energy_release_component_all_max: allRecDaily.energy_release_component_max.max,
            energy_release_component_all_min: allRecDaily.energy_release_component_max.min,
            energy_release_component_all_avg: allRecDaily.energy_release_component_max.avg,

            spread_component_all_max: allRecDaily.spread_component_max.max,
            spread_component_all_min: allRecDaily.spread_component_max.min,
            spread_component_all_avg: allRecDaily.spread_component_max.avg,

            ignition_component_all_max: allRecDaily.ignition_component_max.max,
            ignition_component_all_min: allRecDaily.ignition_component_max.min,
            ignition_component_all_avg: allRecDaily.ignition_component_max.avg,

            burning_index_all_max: allRecDaily.burning_index_max.max,
            burning_index_all_min: allRecDaily.burning_index_max.min,
            burning_index_all_avg: allRecDaily.burning_index_max.avg,

            one_hr_tl_fuel_moisture_all_max: allRecDaily.one_hr_tl_fuel_moisture_min.max,
            one_hr_tl_fuel_moisture_all_min: allRecDaily.one_hr_tl_fuel_moisture_min.min,
            one_hr_tl_fuel_moisture_all_avg: allRecDaily.one_hr_tl_fuel_moisture_min.avg,

            ten_hr_tl_fuel_moisture_all_max: allRecDaily.ten_hr_tl_fuel_moisture_min.max,
            ten_hr_tl_fuel_moisture_all_min: allRecDaily.ten_hr_tl_fuel_moisture_min.min,
            ten_hr_tl_fuel_moisture_all_avg: allRecDaily.ten_hr_tl_fuel_moisture_min.avg,

            hun_hr_tl_fuel_moisture_all_max: allRecDaily.hun_hr_tl_fuel_moisture_min.max,
            hun_hr_tl_fuel_moisture_all_min: allRecDaily.hun_hr_tl_fuel_moisture_min.min,
            hun_hr_tl_fuel_moisture_all_avg: allRecDaily.hun_hr_tl_fuel_moisture_min.avg,

            thou_hr_tl_fuel_moisture_all_max: allRecDaily.thou_hr_tl_fuel_moisture_min.max,
            thou_hr_tl_fuel_moisture_all_min: allRecDaily.thou_hr_tl_fuel_moisture_min.min,
            thou_hr_tl_fuel_moisture_all_avg: allRecDaily.thou_hr_tl_fuel_moisture_min.avg,

            herbaceous_lfi_fuel_moisture_all_max: allRecDaily.herbaceous_lfi_fuel_moisture_max.max,
            herbaceous_lfi_fuel_moisture_all_min: allRecDaily.herbaceous_lfi_fuel_moisture_max.min,
            herbaceous_lfi_fuel_moisture_all_avg: allRecDaily.herbaceous_lfi_fuel_moisture_max.avg,

            woody_lfi_fuel_moisture_all_max: allRecDaily.woody_lfi_fuel_moisture_max.max,
            woody_lfi_fuel_moisture_all_min: allRecDaily.woody_lfi_fuel_moisture_max.min,
            woody_lfi_fuel_moisture_all_avg: allRecDaily.woody_lfi_fuel_moisture_max.avg,
            kbdi_all_max: allRecDaily.kbdi_max.max,
            kbdi_all_min: allRecDaily.kbdi_max.min,
            kbdi_all_avg: allRecDaily.kbdi_max.avg,
            gsi_all_max: allRecDaily.gsi_max.max,
            gsi_all_min: allRecDaily.gsi_max.min,
            gsi_all_avg: allRecDaily.gsi_max.avg,
            
          })
        }
        else{
          return({
            
            ...forecastToConnectLines,
            dateString:moment(`${allRecDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
            date: Number(moment(`${allRecDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
            ...theCurrentDaily,

            energy_release_component_all_max: allRecDaily.energy_release_component_max.max,
            energy_release_component_all_min: allRecDaily.energy_release_component_max.min,
            energy_release_component_all_avg: allRecDaily.energy_release_component_max.avg,
            spread_component_all_max: allRecDaily.spread_component_max.max,
            spread_component_all_min: allRecDaily.spread_component_max.min,
            spread_component_all_avg: allRecDaily.spread_component_max.avg,
            ignition_component_all_max: allRecDaily.ignition_component_max.max,
            ignition_component_all_min: allRecDaily.ignition_component_max.min,
            ignition_component_all_avg: allRecDaily.ignition_component_max.avg,
            burning_index_all_max: allRecDaily.burning_index_max.max,
            burning_index_all_min: allRecDaily.burning_index_max.min,
            burning_index_all_avg: allRecDaily.burning_index_max.avg,
            one_hr_tl_fuel_moisture_all_max: allRecDaily.one_hr_tl_fuel_moisture_min.max,
            one_hr_tl_fuel_moisture_all_min: allRecDaily.one_hr_tl_fuel_moisture_min.min,
            one_hr_tl_fuel_moisture_all_avg: allRecDaily.one_hr_tl_fuel_moisture_min.avg,
            ten_hr_tl_fuel_moisture_all_max: allRecDaily.ten_hr_tl_fuel_moisture_min.max,
            ten_hr_tl_fuel_moisture_all_min: allRecDaily.ten_hr_tl_fuel_moisture_min.min,
            ten_hr_tl_fuel_moisture_all_avg: allRecDaily.ten_hr_tl_fuel_moisture_min.avg,
            hun_hr_tl_fuel_moisture_all_max: allRecDaily.hun_hr_tl_fuel_moisture_min.max,
            hun_hr_tl_fuel_moisture_all_min: allRecDaily.hun_hr_tl_fuel_moisture_min.min,
            hun_hr_tl_fuel_moisture_all_avg: allRecDaily.hun_hr_tl_fuel_moisture_min.avg,
            thou_hr_tl_fuel_moisture_all_max: allRecDaily.thou_hr_tl_fuel_moisture_min.max,
            thou_hr_tl_fuel_moisture_all_min: allRecDaily.thou_hr_tl_fuel_moisture_min.min,
            thou_hr_tl_fuel_moisture_all_avg: allRecDaily.thou_hr_tl_fuel_moisture_min.avg,
            herbaceous_lfi_fuel_moisture_all_max: allRecDaily.herbaceous_lfi_fuel_moisture_max.max,
            herbaceous_lfi_fuel_moisture_all_min: allRecDaily.herbaceous_lfi_fuel_moisture_max.min,
            herbaceous_lfi_fuel_moisture_all_avg: allRecDaily.herbaceous_lfi_fuel_moisture_max.avg,
            woody_lfi_fuel_moisture_all_max: allRecDaily.woody_lfi_fuel_moisture_max.max,
            woody_lfi_fuel_moisture_all_min: allRecDaily.woody_lfi_fuel_moisture_max.min,
            woody_lfi_fuel_moisture_all_avg: allRecDaily.woody_lfi_fuel_moisture_max.avg,
            kbdi_all_max: allRecDaily.kbdi_max.max,
            kbdi_all_min: allRecDaily.kbdi_max.min,
            kbdi_all_avg: allRecDaily.kbdi_max.avg,
            gsi_all_max: allRecDaily.gsi_max.max,
            gsi_all_min: allRecDaily.gsi_max.min,
            gsi_all_avg: allRecDaily.gsi_max.avg,
          })
        }
      }
      else{
        if(theCurrentDaily){
          return({
            dateString:moment(theCurrentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
            date: Number(moment(theCurrentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
            energy_release_component_max_f: theCurrentDaily.energy_release_component_max,
            burning_index_max_f: theCurrentDaily.burning_index_max,
            gsi_f: theCurrentDaily.gsi,
            herbaceous_lfi_fuel_moisture_f: theCurrentDaily.herbaceous_lfi_fuel_moisture,
            hun_hr_tl_fuel_moisture_min_f: theCurrentDaily.hun_hr_tl_fuel_moisture_min,
            ignition_component_max_f: theCurrentDaily.ignition_component_max,
            kbdi_f: theCurrentDaily.kbdi,
            one_hr_tl_fuel_moisture_min_f: theCurrentDaily.one_hr_tl_fuel_moisture_min,
            spread_component_max_f: theCurrentDaily.spread_component_max,
            station_id: theCurrentDaily.station_id,
            ten_hr_tl_fuel_moisture_min_f: theCurrentDaily.ten_hr_tl_fuel_moisture_min,
            thou_hr_tl_fuel_moisture_min_f: theCurrentDaily.thou_hr_tl_fuel_moisture_min,
            woody_lfi_fuel_moisture_f: theCurrentDaily.woody_lfi_fuel_moisture,

            energy_release_component_all_max: allRecDaily.energy_release_component_max.max,
            energy_release_component_all_min: allRecDaily.energy_release_component_max.min,
            energy_release_component_all_avg: allRecDaily.energy_release_component_max.avg,
            spread_component_all_max: allRecDaily.spread_component_max.max,
            spread_component_all_min: allRecDaily.spread_component_max.min,
            spread_component_all_avg: allRecDaily.spread_component_max.avg,
            ignition_component_all_max: allRecDaily.ignition_component_max.max,
            ignition_component_all_min: allRecDaily.ignition_component_max.min,
            ignition_component_all_avg: allRecDaily.ignition_component_max.avg,
            burning_index_all_max: allRecDaily.burning_index_max.max,
            burning_index_all_min: allRecDaily.burning_index_max.min,
            burning_index_all_avg: allRecDaily.burning_index_max.avg,
            one_hr_tl_fuel_moisture_all_max: allRecDaily.one_hr_tl_fuel_moisture_min.max,
            one_hr_tl_fuel_moisture_all_min: allRecDaily.one_hr_tl_fuel_moisture_min.min,
            one_hr_tl_fuel_moisture_all_avg: allRecDaily.one_hr_tl_fuel_moisture_min.avg,
            ten_hr_tl_fuel_moisture_all_max: allRecDaily.ten_hr_tl_fuel_moisture_min.max,
            ten_hr_tl_fuel_moisture_all_min: allRecDaily.ten_hr_tl_fuel_moisture_min.min,
            ten_hr_tl_fuel_moisture_all_avg: allRecDaily.ten_hr_tl_fuel_moisture_min.avg,
            hun_hr_tl_fuel_moisture_all_max: allRecDaily.hun_hr_tl_fuel_moisture_min.max,
            hun_hr_tl_fuel_moisture_all_min: allRecDaily.hun_hr_tl_fuel_moisture_min.min,
            hun_hr_tl_fuel_moisture_all_avg: allRecDaily.hun_hr_tl_fuel_moisture_min.avg,
            thou_hr_tl_fuel_moisture_all_max: allRecDaily.thou_hr_tl_fuel_moisture_min.max,
            thou_hr_tl_fuel_moisture_all_min: allRecDaily.thou_hr_tl_fuel_moisture_min.min,
            thou_hr_tl_fuel_moisture_all_avg: allRecDaily.thou_hr_tl_fuel_moisture_min.avg,
            herbaceous_lfi_fuel_moisture_all_max: allRecDaily.herbaceous_lfi_fuel_moisture_max.max,
            herbaceous_lfi_fuel_moisture_all_min: allRecDaily.herbaceous_lfi_fuel_moisture_max.min,
            herbaceous_lfi_fuel_moisture_all_avg: allRecDaily.herbaceous_lfi_fuel_moisture_max.avg,
            woody_lfi_fuel_moisture_all_max: allRecDaily.woody_lfi_fuel_moisture_max.max,
            woody_lfi_fuel_moisture_all_min: allRecDaily.woody_lfi_fuel_moisture_max.min,
            woody_lfi_fuel_moisture_all_avg: allRecDaily.woody_lfi_fuel_moisture_max.avg,
            kbdi_all_max: allRecDaily.kbdi_max.max,
            kbdi_all_min: allRecDaily.kbdi_max.min,
            kbdi_all_avg: allRecDaily.kbdi_max.avg,
            gsi_all_max: allRecDaily.gsi_max.max,
            gsi_all_min: allRecDaily.gsi_max.min,
            gsi_all_avg: allRecDaily.gsi_max.avg,
          })
        }
        else{
          /* gap in current max/min/avg value - black line*/
          return({
            dateString:moment(`${allRecDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
            date: Number(moment(`${allRecDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
            energy_release_component_max_f: null,
            burning_index_max_f: null,
            gsi_f: null,
            herbaceous_lfi_fuel_moisture_f: null,
            hun_hr_tl_fuel_moisture_min_f: null,
            ignition_component_max_f: null,
            kbdi_f: null,
            one_hr_tl_fuel_moisture_min_f: null,
            spread_component_max_f: null,
            station_id: allRecDaily.station_id,
            ten_hr_tl_fuel_moisture_min_f: null,
            thou_hr_tl_fuel_moisture_min_f: null,
            woody_lfi_fuel_moisture_f: null,

            energy_release_component_all_max: allRecDaily.energy_release_component_max.max,
            energy_release_component_all_min: allRecDaily.energy_release_component_max.min,
            energy_release_component_all_avg: allRecDaily.energy_release_component_max.avg,
            spread_component_all_max: allRecDaily.spread_component_max.max,
            spread_component_all_min: allRecDaily.spread_component_max.min,
            spread_component_all_avg: allRecDaily.spread_component_max.avg,
            ignition_component_all_max: allRecDaily.ignition_component_max.max,
            ignition_component_all_min: allRecDaily.ignition_component_max.min,
            ignition_component_all_avg: allRecDaily.ignition_component_max.avg,
            burning_index_all_max: allRecDaily.burning_index_max.max,
            burning_index_all_min: allRecDaily.burning_index_max.min,
            burning_index_all_avg: allRecDaily.burning_index_max.avg,
            one_hr_tl_fuel_moisture_all_max: allRecDaily.one_hr_tl_fuel_moisture_min.max,
            one_hr_tl_fuel_moisture_all_min: allRecDaily.one_hr_tl_fuel_moisture_min.min,
            one_hr_tl_fuel_moisture_all_avg: allRecDaily.one_hr_tl_fuel_moisture_min.avg,
            ten_hr_tl_fuel_moisture_all_max: allRecDaily.ten_hr_tl_fuel_moisture_min.max,
            ten_hr_tl_fuel_moisture_all_min: allRecDaily.ten_hr_tl_fuel_moisture_min.min,
            ten_hr_tl_fuel_moisture_all_avg: allRecDaily.ten_hr_tl_fuel_moisture_min.avg,
            hun_hr_tl_fuel_moisture_all_max: allRecDaily.hun_hr_tl_fuel_moisture_min.max,
            hun_hr_tl_fuel_moisture_all_min: allRecDaily.hun_hr_tl_fuel_moisture_min.min,
            hun_hr_tl_fuel_moisture_all_avg: allRecDaily.hun_hr_tl_fuel_moisture_min.avg,
            thou_hr_tl_fuel_moisture_all_max: allRecDaily.thou_hr_tl_fuel_moisture_min.max,
            thou_hr_tl_fuel_moisture_all_min: allRecDaily.thou_hr_tl_fuel_moisture_min.min,
            thou_hr_tl_fuel_moisture_all_avg: allRecDaily.thou_hr_tl_fuel_moisture_min.avg,
            herbaceous_lfi_fuel_moisture_all_max: allRecDaily.herbaceous_lfi_fuel_moisture_max.max,
            herbaceous_lfi_fuel_moisture_all_min: allRecDaily.herbaceous_lfi_fuel_moisture_max.min,
            herbaceous_lfi_fuel_moisture_all_avg: allRecDaily.herbaceous_lfi_fuel_moisture_max.avg,
            woody_lfi_fuel_moisture_all_max: allRecDaily.woody_lfi_fuel_moisture_max.max,
            woody_lfi_fuel_moisture_all_min: allRecDaily.woody_lfi_fuel_moisture_max.min,
            woody_lfi_fuel_moisture_all_avg: allRecDaily.woody_lfi_fuel_moisture_max.avg,
            kbdi_all_max: allRecDaily.kbdi_max.max,
            kbdi_all_min: allRecDaily.kbdi_max.min,
            kbdi_all_avg: allRecDaily.kbdi_max.avg,
            gsi_all_max: allRecDaily.gsi_max.max,
            gsi_all_min: allRecDaily.gsi_max.min,
            gsi_all_avg: allRecDaily.gsi_max.avg,
          })
        }
      }

		});

    /* if current missing some items */

    if(lastMD!== dailies[dailies.length-1].day || firstMD!== dailies[0].day){
      /*modify to fill in the min/max/avg of the half after current is empty*/
      const findIndex = dailies.findIndex((daily:any)=>daily.day === lastMD);
      const newSlice = dailies.slice(findIndex+1);
      const toAppendDaily = newSlice.map((newDaily:any)=>{
        return({
          dateString:moment(`${newDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
          date: Number(moment(`${newDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
          energy_release_component_all_max: newDaily.energy_release_component_max.max,
          energy_release_component_all_min: newDaily.energy_release_component_max.min,
          energy_release_component_all_avg: newDaily.energy_release_component_max.avg,
          spread_component_all_max: newDaily.spread_component_max.max,
          spread_component_all_min: newDaily.spread_component_max.min,
          spread_component_all_avg: newDaily.spread_component_max.avg,
          ignition_component_all_max: newDaily.ignition_component_max.max,
          ignition_component_all_min: newDaily.ignition_component_max.min,
          ignition_component_all_avg: newDaily.ignition_component_max.avg,
          burning_index_all_max: newDaily.burning_index_max.max,
          burning_index_all_min: newDaily.burning_index_max.min,
          burning_index_all_avg: newDaily.burning_index_max.avg,
          one_hr_tl_fuel_moisture_all_max: newDaily.one_hr_tl_fuel_moisture_min.max,
          one_hr_tl_fuel_moisture_all_min: newDaily.one_hr_tl_fuel_moisture_min.min,
          one_hr_tl_fuel_moisture_all_avg: newDaily.one_hr_tl_fuel_moisture_min.avg,
          ten_hr_tl_fuel_moisture_all_max: newDaily.ten_hr_tl_fuel_moisture_min.max,
          ten_hr_tl_fuel_moisture_all_min: newDaily.ten_hr_tl_fuel_moisture_min.min,
          ten_hr_tl_fuel_moisture_all_avg: newDaily.ten_hr_tl_fuel_moisture_min.avg,
          hun_hr_tl_fuel_moisture_all_max: newDaily.hun_hr_tl_fuel_moisture_min.max,
          hun_hr_tl_fuel_moisture_all_min: newDaily.hun_hr_tl_fuel_moisture_min.min,
          hun_hr_tl_fuel_moisture_all_avg: newDaily.hun_hr_tl_fuel_moisture_min.avg,
          thou_hr_tl_fuel_moisture_all_max: newDaily.thou_hr_tl_fuel_moisture_min.max,
          thou_hr_tl_fuel_moisture_all_min: newDaily.thou_hr_tl_fuel_moisture_min.min,
          thou_hr_tl_fuel_moisture_all_avg: newDaily.thou_hr_tl_fuel_moisture_min.avg,
          herbaceous_lfi_fuel_moisture_all_max: newDaily.herbaceous_lfi_fuel_moisture_max.max,
          herbaceous_lfi_fuel_moisture_all_min: newDaily.herbaceous_lfi_fuel_moisture_max.min,
          herbaceous_lfi_fuel_moisture_all_avg: newDaily.herbaceous_lfi_fuel_moisture_max.avg,
          woody_lfi_fuel_moisture_all_max: newDaily.woody_lfi_fuel_moisture_max.max,
          woody_lfi_fuel_moisture_all_min: newDaily.woody_lfi_fuel_moisture_max.min,
          woody_lfi_fuel_moisture_all_avg: newDaily.woody_lfi_fuel_moisture_max.avg,
          kbdi_all_max: newDaily.kbdi_max.max,
          kbdi_all_min: newDaily.kbdi_max.min,
          kbdi_all_avg: newDaily.kbdi_max.avg,
          gsi_all_max: newDaily.gsi_max.max,
          gsi_all_min: newDaily.gsi_max.min,
          gsi_all_avg: newDaily.gsi_max.avg,
          station_id: newDaily.station_id,
        })
      });
      /*modify to fill in the min/max/avg of the half before current is empty*/
      const findIndexWhereCurrentBegins = dailies.findIndex((daily:any)=>daily.day === firstMD);
      const newSliceWhereCurrentBegins = dailies.slice(0,findIndexWhereCurrentBegins+1);
      const addTheFirstSlice = newSliceWhereCurrentBegins.map((newDaily:any)=>{
        return({
          dateString:moment(`${newDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
          date: Number(moment(`${newDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
          energy_release_component_all_max: newDaily.energy_release_component_max.max,
          energy_release_component_all_min: newDaily.energy_release_component_max.min,
          energy_release_component_all_avg: newDaily.energy_release_component_max.avg,
          spread_component_all_max: newDaily.spread_component_max.max,
          spread_component_all_min: newDaily.spread_component_max.min,
          spread_component_all_avg: newDaily.spread_component_max.avg,
          ignition_component_all_max: newDaily.ignition_component_max.max,
          ignition_component_all_min: newDaily.ignition_component_max.min,
          ignition_component_all_avg: newDaily.ignition_component_max.avg,
          burning_index_all_max: newDaily.burning_index_max.max,
          burning_index_all_min: newDaily.burning_index_max.min,
          burning_index_all_avg: newDaily.burning_index_max.avg,
          one_hr_tl_fuel_moisture_all_max: newDaily.one_hr_tl_fuel_moisture_min.max,
          one_hr_tl_fuel_moisture_all_min: newDaily.one_hr_tl_fuel_moisture_min.min,
          one_hr_tl_fuel_moisture_all_avg: newDaily.one_hr_tl_fuel_moisture_min.avg,
          ten_hr_tl_fuel_moisture_all_max: newDaily.ten_hr_tl_fuel_moisture_min.max,
          ten_hr_tl_fuel_moisture_all_min: newDaily.ten_hr_tl_fuel_moisture_min.min,
          ten_hr_tl_fuel_moisture_all_avg: newDaily.ten_hr_tl_fuel_moisture_min.avg,
          hun_hr_tl_fuel_moisture_all_max: newDaily.hun_hr_tl_fuel_moisture_min.max,
          hun_hr_tl_fuel_moisture_all_min: newDaily.hun_hr_tl_fuel_moisture_min.min,
          hun_hr_tl_fuel_moisture_all_avg: newDaily.hun_hr_tl_fuel_moisture_min.avg,
          thou_hr_tl_fuel_moisture_all_max: newDaily.thou_hr_tl_fuel_moisture_min.max,
          thou_hr_tl_fuel_moisture_all_min: newDaily.thou_hr_tl_fuel_moisture_min.min,
          thou_hr_tl_fuel_moisture_all_avg: newDaily.thou_hr_tl_fuel_moisture_min.avg,
          herbaceous_lfi_fuel_moisture_all_max: newDaily.herbaceous_lfi_fuel_moisture_max.max,
          herbaceous_lfi_fuel_moisture_all_min: newDaily.herbaceous_lfi_fuel_moisture_max.min,
          herbaceous_lfi_fuel_moisture_all_avg: newDaily.herbaceous_lfi_fuel_moisture_max.avg,
          woody_lfi_fuel_moisture_all_max: newDaily.woody_lfi_fuel_moisture_max.max,
          woody_lfi_fuel_moisture_all_min: newDaily.woody_lfi_fuel_moisture_max.min,
          woody_lfi_fuel_moisture_all_avg: newDaily.woody_lfi_fuel_moisture_max.avg,
          kbdi_all_max: newDaily.kbdi_max.max,
          kbdi_all_min: newDaily.kbdi_max.min,
          kbdi_all_avg: newDaily.kbdi_max.avg,
          gsi_all_max: newDaily.gsi_max.max,
          gsi_all_min: newDaily.gsi_max.min,
          gsi_all_avg: newDaily.gsi_max.avg,
          station_id: newDaily.station_id,
        })
      });
      const nfdrsCurrent = [...addTheFirstSlice, ...gapFilling, ...toAppendDaily].toSorted((a,b)=>a.date -b.date)
      setNfdrsCurrentValues(nfdrsCurrent)
    }
    else{
      setNfdrsCurrentValues(gapFilling)
    }
  },[currStation?.time_zone_offset, date]);

    //formats for nfdrs ones
  const formatChartForWX = useCallback((currentData: any, dailies:any, yr?:string)=>{
    let yearSelected:string='';

    if(yr){
      yearSelected=yr
    }else{
      yearSelected= moment(date).format('YYYY');
    } 
    setYrSelected(yearSelected)
    const lastItemCurrent = currentData[currentData.length-1];
    const lastMD = moment(lastItemCurrent?.summary_date).utcOffset(currStation?.time_zone_offset, true).format('MM-DD')

    const firstItemCurrent = currentData[0];
    const firstMD = moment(firstItemCurrent?.summary_date).utcOffset(currStation?.time_zone_offset, true).format('MM-DD')
    /* need to combine current daily values with the daily from historic*/
    /* the chart takes the following:
      vapor_press_def_max_f would be the current forecast line
      vapor_press_def_max would be the current obs line
      vapor_press_def_all_max is for all period of record line

    */
    /*below will find the index of the first instance of forecast for the current line (black line)*/
    const firstForecastForCurrentIndex = currentData.findIndex((currentDaily:any)=> currentDaily.observation_type==='F');
    /*this will get the object (hopefully obser type) to fill in the line between forecast and obs*/
    const theObservationBeforeForecastObj = currentData[firstForecastForCurrentIndex-1];
    
    const slicedAllRecordDaily = dailies.slice(dailies.findIndex((daily:any)=>daily.day === firstMD),dailies.findIndex((daily:any)=>daily.day === lastMD)+1)

    let gapFilling = slicedAllRecordDaily.map((allRecDaily: any, index: number, currentArray: any) => {

    const theCurrentDaily = currentData.find((currentDaily:any)=> moment(currentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD') === allRecDaily.day);
      if(theCurrentDaily?.observation_type==='O'){
        let forecastToConnectLines:any;
        /*below is to add a forecast object for the last observation object found to fill in the 
        gap on the chart between obs and forecast*/
        if(theCurrentDaily.summary_date===theObservationBeforeForecastObj?.summary_date){
          forecastToConnectLines={
            vapor_press_def_max_f: theCurrentDaily.vapor_press_def_max,
            vapor_press_def_min_f: theCurrentDaily.vapor_press_def_min,
            station_id: theCurrentDaily.station_id,
          }
        }
        if(theCurrentDaily){
          return({
            ...forecastToConnectLines,
            dateString:moment(theCurrentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
            date: Number(moment(theCurrentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
            ...theCurrentDaily,
            vapor_press_def_max_all_max: allRecDaily.vapor_press_def_max.max,
            vapor_press_def_max_all_min: allRecDaily.vapor_press_def_max.min,
            vapor_press_def_max_all_avg: allRecDaily.vapor_press_def_max.avg,

            vapor_press_def_min_all_max: allRecDaily.vapor_press_def_min.max,
            vapor_press_def_min_all_min: allRecDaily.vapor_press_def_min.min,
            vapor_press_def_min_all_avg: allRecDaily.vapor_press_def_min.avg,
            station_id: allRecDaily.station_id,
            
          })
        }
        else{
          return({
            
            ...forecastToConnectLines,
            dateString:moment(`${allRecDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
            date: Number(moment(`${allRecDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
            ...theCurrentDaily,

            vapor_press_def_max_all_max: allRecDaily.vapor_press_def_max.max,
            vapor_press_def_max_all_min: allRecDaily.vapor_press_def_max.min,
            vapor_press_def_max_all_avg: allRecDaily.vapor_press_def_max.avg,

            vapor_press_def_min_all_max: allRecDaily.vapor_press_def_min.max,
            vapor_press_def_min_all_min: allRecDaily.vapor_press_def_min.min,
            vapor_press_def_min_all_avg: allRecDaily.vapor_press_def_min.avg,
            station_id: allRecDaily.station_id,
          })
        }
      }
      else{
        if(theCurrentDaily){
          return({
            dateString:moment(theCurrentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
            date: Number(moment(theCurrentDaily.summary_date).utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
            station_id: theCurrentDaily.station_id,
            vapor_press_def_max_f: theCurrentDaily.vapor_press_def_max,
            vapor_press_def_min_f: theCurrentDaily.vapor_press_def_min,

            vapor_press_def_max_all_max: allRecDaily.vapor_press_def_max.max,
            vapor_press_def_max_all_min: allRecDaily.vapor_press_def_max.min,
            vapor_press_def_max_all_avg: allRecDaily.vapor_press_def_max.avg,

            vapor_press_def_min_all_max: allRecDaily.vapor_press_def_min.max,
            vapor_press_def_min_all_min: allRecDaily.vapor_press_def_min.min,
            vapor_press_def_min_all_avg: allRecDaily.vapor_press_def_min.avg,
            
          })
        }
        else{
          /* gap in current max/min/avg value - black line*/
          return({
            dateString:moment(`${allRecDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
            date: Number(moment(`${allRecDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
            station_id: allRecDaily.station_id,

            vapor_press_def_max_f: null,
            vapor_press_def_min_f: null,

            vapor_press_def_max_all_max: allRecDaily.vapor_press_def_max.max,
            vapor_press_def_max_all_min: allRecDaily.vapor_press_def_max.min,
            vapor_press_def_max_all_avg: allRecDaily.vapor_press_def_max.avg,
            
            vapor_press_def_min_all_max: allRecDaily.vapor_press_def_min.max,
            vapor_press_def_min_all_min: allRecDaily.vapor_press_def_min.min,
            vapor_press_def_min_all_avg: allRecDaily.vapor_press_def_min.avg,
          })
        }
      }

		});

    /* if current missing some items */

    if(lastMD!== dailies[dailies.length-1].day || firstMD!== dailies[0].day){
      /*modify to fill in the min/max/avg of the half after current is empty*/
      const findIndex = dailies.findIndex((daily:any)=>daily.day === lastMD);
      const newSlice = dailies.slice(findIndex+1);
      const toAppendDaily = newSlice.map((newDaily:any)=>{
        return({
          dateString:moment(`${newDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
          date: Number(moment(`${newDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
          station_id: newDaily.station_id,
          vapor_press_def_max_all_max: newDaily.vapor_press_def_max.max,
          vapor_press_def_max_all_min: newDaily.vapor_press_def_max.min,
          vapor_press_def_max_all_avg: newDaily.vapor_press_def_max.avg,
            
          vapor_press_def_min_all_max: newDaily.vapor_press_def_min.max,
          vapor_press_def_min_all_min: newDaily.vapor_press_def_min.min,
          vapor_press_def_min_all_avg: newDaily.vapor_press_def_min.avg,
        })
      });
      /*modify to fill in the min/max/avg of the half before current is empty*/
      const findIndexWhereCurrentBegins = dailies.findIndex((daily:any)=>daily.day === firstMD);
      const newSliceWhereCurrentBegins = dailies.slice(0,findIndexWhereCurrentBegins+1);
      const addTheFirstSlice = newSliceWhereCurrentBegins.map((newDaily:any)=>{
        return({
          dateString:moment(`${newDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('MM-DD-YY'),
          date: Number(moment(`${newDaily.day}-${yearSelected} 13`, 'MM-DD-YYYY HH').utc().utcOffset(currStation?.time_zone_offset, true).format('x')),
          vapor_press_def_min_all_max: newDaily.vapor_press_def_min.max,
          vapor_press_def_min_all_min: newDaily.vapor_press_def_min.min,
          vapor_press_def_min_all_avg: newDaily.vapor_press_def_min.avg,
          station_id: newDaily.station_id,
        })
      });
      const wxCurrent = [...addTheFirstSlice, ...gapFilling, ...toAppendDaily].toSorted((a,b)=>a.date -b.date);
      setWxCurrentValues(wxCurrent)
    }
    else{
      setWxCurrentValues(gapFilling);
    }
  },[currStation?.time_zone_offset, date]);
  /*get percentile lines*/
  const getPercentileLines: any= useCallback((varRequest:any, counter: number) => {
    setCallsComplete(false);
    (async () => {
      try {
        const response: any = await getPercentileLevels(varRequest, addAlert, state.user);
        if (response) {
          setCallsComplete(true)
          setPercentileLines(response[0]);
          setPercLineFailCount(0)
          return 0;
        }
        else{
          setCallsComplete(true);
          setPercLineFailCount(counter+1)
          return counter+1;
        }

      }
      catch (error) {
        setCallsComplete(true)
        setPercLineFailCount(counter+1)
        console.log('error', error)
        return counter+1;
      }
    })();
  }, [addAlert, state?.user]);

  /*get the dailies for the current lines*/
  const getCurrent: any = useCallback((varRequest:any, alwaysData:any, failCount: number, yr?:string) => {
    setCallsComplete(false);
    (async () => {
      try {
        const response: any = await getNfdrMinMax(varRequest, addAlert, state.user);
        if (response?.length>0) {
          formatForChart(response, alwaysData, yr);
          setCallsComplete(true);
          setNfdrsCurrFailCount(0);
          return 0;
        }
        else{
          setCallsComplete(true);
          setNfdrsCurrentValues([]);
          setNfdrsCurrFailCount(failCount+1)
          return failCount +1;
        }
      }
      catch (error) {
        console.log('error', error);
        setNfdrsCurrentValues([])
        setCallsComplete(true);
        setNfdrsCurrFailCount(failCount+1)
        return failCount +1;
      }
    })();
  }, [addAlert, formatForChart, state?.user, setNfdrsCurrFailCount]);

  const getWxCurrentValues: any= useCallback((varRequest:any, alwaysData:any, count: number, yr?:string) => {
    setCallsComplete(false);
    (async () => {
      try {
        const response: any = await getWeatherMinMax(varRequest, addAlert, state.user);
        if (response?.data?.length>0) {
          formatChartForWX(response.data, alwaysData, yr);
          setCallsComplete(true);
          setWxCurrFailCount(0)
          return 0;
        }
        else{
          setCallsComplete(true);
          setWxCurrentValues([])
          setWxCurrFailCount(count+1);
          return count+1;
        }
      }
      catch (error) {
        console.log('error', error);
        setCallsComplete(true);
        setWxCurrentValues([])
        setWxCurrFailCount(count+1)
        return count+1;
      }
    })();
  }, [addAlert, formatChartForWX, state?.user, setWxCurrFailCount]);

  /* call to get any chart avg, max, min*/
  const getChartData:any = useCallback((query: string, variables: any, varsPerc:any, failCounter: number) => {
    setCallsComplete(false);
    (async () => {
      try {
        const queryToSend = `query GetPercentileAvgMinMax(
          $stationId: String!
          $fuelModel: FuelModelTypes!
          $startYear: ClimatologyYear
          $endYear: ClimatologyYear
          $startMonthDay: ClimatologyMonthDay
          $endMonthDay: ClimatologyMonthDay
          $startHour: TimeHour
          $endHour: TimeHour
          $dateTimeFormat: DateTimeFormat
          $visibilitySettings: TriState
      ) {
          percentileAvgMinMax(
              stationIds: $stationId
              fuelModel: $fuelModel
              visibilitySettings: $visibilitySettings
              climatology: {
                  startYear: $startYear
                  endYear: $endYear
                  startMonthDay: $startMonthDay
                  endMonthDay: $endMonthDay
                  startHour: $startHour
                  endHour: $endHour
                  dateTimeFormat: $dateTimeFormat
              }
              page: 0
              per_page: 300000
          ) {
              _metadata {
                  page
                  per_page
                  total_count
                  page_count
              }
              data {
                  station_id
                  day
                  ${query}
              }
          }
      }
        `;
        const response: any = await getCharts(queryToSend, variables, addAlert, state.user);
        if (response?.length>=1) {
          setCallsComplete(false)
          setFourMainChartData(response);
          const varsCurrentDaily = {
            stationIds: station?.station_id.toString(),
            zoomLevel: 5,
            startDate: `${moment(date).utc().format('YYYY')}-01-01`,
            endDate: `${moment(date).utc().format('YYYY')}-12-31`,
            fuelModel: variables.fuelModel,
            page: 0,
            perPage: 300000,
            dateTimeFormat: 'LocalStationTime',
          };
          //below is the current values for nfdrs values
          let failCounterCurrent = 0;
          let wxFailCounterCurrent = 0;
          let percCounterFail = 0;

          getCurrent(varsCurrentDaily, response, failCounterCurrent);

          const varsCurrentWX = {
            stationIds: station?.station_id.toString(),
            zoomLevel: 5,
            startDate: `${moment(date).utc().format('YYYY')}-01-01`,
            endDate: `${moment(date).utc().format('YYYY')}-12-31`,
            page: 0,
            perPage: 300000,
            sortOrder: 'ASC',
            sortBy: 'summary_date'
          };
          
          getWxCurrentValues(varsCurrentWX, response, wxFailCounterCurrent);

          const varsPercentileLvl ={
            ...varsPerc,
            percentileLevels: '90,97,3,10',
            visibilitySettings: 'ALL'
          }
          
          getPercentileLines(varsPercentileLvl, percCounterFail);
          return 0;
        }
        else{
          setFourMainChartData([])
          addAlert({
            id:'',
            message: alertMessages.retrievingStationData,
            type:'error-info',
            icon:'fa-exclamation-triangle',
            clear: 'persist'
          });
          setCallsComplete(true);
          return failCounter+1;
        }
      }
      catch (error) {
        setFourMainChartData([])
        console.log('error', error);
        setCallsComplete(true);
        return failCounter+1;
      }
    })();
  }, [addAlert, date, getCurrent, station?.station_id, 
    getPercentileLines, getWxCurrentValues, state?.user]);
  
  /*initial call for the four main charts*/
  useEffect(() => {
    if (currStation) {
      setStartOfChartX(moment([moment(date).format('YYYY'),0,1 ]).utc().utcOffset(currStation?.time_zone_offset, true).format('x'));
      setEndOfChartX(moment([moment(date).format('YYYY'),11,31 ]).utc().utcOffset(currStation?.time_zone_offset, true).format('x'));
      const varsPercentiles = {
        stationId: currStation?.station_id.toString(),
        fuelModel: fuelModel,
        startYear: currStation?.period_record_start? moment(currStation?.period_record_start).utc().format('YYYY'): '2005',
        endYear: '2024',//may need to change back later - currStation?.period_record_stop? moment(currStation?.period_record_stop).utc().format('YYYY'):'2022',
        startMonthDay: '01-01',
        endMonthDay: '12-31',
        startHour: "0",
        endHour: "24",
        dateTimeFormat: 'LocalStationTime',
        visibilitySettings:'ALL',
      };
      const varsOther = {
        stationId: currStation?.station_id.toString(),
        fuelModel: fuelModel,
        startYear: currStation?.period_record_start? moment(currStation?.period_record_start).utc().format('YYYY'): '2005',
        endYear: currStation?.period_record_stop? moment(currStation?.period_record_stop).utc().format('YYYY'):'2022',
        startMonthDay: '01-01',
        endMonthDay: '12-31',
        startHour: "0",
        endHour: "24",
        dateTimeFormat: 'LocalStationTime',
        visibilitySettings: 'ALL',
      };
      let failCounterCurrent = 0;
      failCounterCurrent = getChartData(mainQuery, varsOther, varsPercentiles, failCounterCurrent);
      if (failCounterCurrent > 0 && failCounterCurrent < 4) {
        while (failCounterCurrent > 0 && failCounterCurrent < 4) {
          failCounterCurrent = getChartData(mainQuery, varsOther, varsPercentiles, failCounterCurrent);
        }
      }
    }
    else{
      setCurrStation(station)
    }
    /*below is for four main chart formatting*/
  }, [fourMainChartData?.length, 
    currStation, date, fuelModel, getChartData, station]);

  useEffect(()=>{
    if(nfdrsCurrFailCount>0 && nfdrsCurrFailCount<4 && fourMainChartData?.length>0){
      const varsCurrentDaily = {
        stationIds: station?.station_id.toString(),
        zoomLevel: 5,
        startDate: `${moment(date).utc().format('YYYY')}-01-01`,
        endDate: `${moment(date).utc().format('YYYY')}-12-31`,
        fuelModel: fuelModel,
        page: 0,
        perPage: 300000,
        dateTimeFormat: 'LocalStationTime',
      };
      getCurrent(varsCurrentDaily, fourMainChartData, nfdrsCurrFailCount)
    }

    if (wxCurrFailCount > 0 && wxCurrFailCount < 4 && fourMainChartData?.length > 0) {
      const varsCurrentWX = {
        stationIds: station?.station_id.toString(),
        zoomLevel: 5,
        startDate: `${moment(date).utc().format('YYYY')}-01-01`,
        endDate: `${moment(date).utc().format('YYYY')}-12-31`,
        page: 0,
        perPage: 300000,
        sortOrder: 'ASC',
        sortBy: 'summary_date'
      };
      getWxCurrentValues(varsCurrentWX, fourMainChartData, nfdrsCurrFailCount)
    }
    if (percLineFailCount > 0 && percLineFailCount < 4 && fourMainChartData?.length > 0) {
      const varsPercentileLvl = {
        stationId: currStation?.station_id.toString(),
        fuelModel: fuelModel,
        startYear: currStation?.period_record_start ? moment(currStation?.period_record_start).utc().format('YYYY') : '2005',
        endYear: '2024',
        startMonthDay: '01-01',
        endMonthDay: '12-31',
        startHour: "0",
        endHour: "24",
        dateTimeFormat: 'LocalStationTime',
        percentileLevels: '90,97,3,10',
        visibilitySettings: 'ALL'
      }
      getPercentileLines(varsPercentileLvl, percLineFailCount);
    }
  },[currStation?.period_record_start, currStation?.station_id, date, fourMainChartData,
    fuelModel, getCurrent, getPercentileLines, getWxCurrentValues, nfdrsCurrFailCount, 
    percLineFailCount, station?.station_id, wxCurrFailCount

  ])

  // Merge arrays based on both date and id
  const mergeByDateAndId = useCallback((array1, array2) => {
    const merged = {};

    // Helper to add/merge objects into the dictionary
    const addToMap = (item:any) => {
      const key = `${item.station_id}-${item.dateString}`;
      if (merged[key]) {
        // merge exist
        merged[key] = { ...merged[key], ...item };
      } else {
        // create new
        merged[key] = { ...item };
      }
    };

    // Process both arrays
    array1.forEach(addToMap);
    array2.forEach(addToMap);
    return Object.values(merged);

  }, []);

  useEffect(()=>{
    if(nfdrsCurrentValues?.length>1 && wxCurrentValues?.length>1 && currStation && date){
      const combinedArrays:any = mergeByDateAndId(nfdrsCurrentValues, wxCurrentValues);
      setDataUsedForCharts(combinedArrays)
    }
  },[nfdrsCurrentValues, wxCurrentValues, currStation, date, setDataUsedForCharts, mergeByDateAndId]);

  useEffect(()=>{
    if(location?.state){
      const fromPath: any = location.state
      if(currStation?.station_id?.toString() !== fromPath.station_id?.toString()){
        setCurrStation(fromPath)
      }
    }
  },[location, currStation?.station_id])

  /*function to add tick labels on charts*/
  const getTicks = useCallback((date: any, currStation:any)=>{
    let ticks: Number[] = [];
    let i = moment([moment(date).format('YYYY'), 0, 1]).utc().utcOffset(currStation?.time_zone_offset, true);
    let end = moment([moment(date).format('YYYY'), 11, 31]).utc().utcOffset(currStation?.time_zone_offset, true)
    ticks.push(Number(i.format('x')));
    let j = 0;
    while (i <= end) {
      j = j + 1;
      ticks.push(Number(moment(i).add(1, 'month').format('x')))
      i = moment(moment(i).add(1, 'month'))
    };
    ticks.pop();
    setXTicks(ticks)
  },[]);

  /* need initial ticks*/
  useEffect(() => {
    if (xTicks.length < 12 && date && currStation) {
      getTicks(date, currStation)
    }
  },[date, currStation, xTicks, getTicks])

  const removeChart =useCallback((id:any)=>{
    setAdditionalChartObj((prevData)=> prevData.filter((item)=>item.id!==id));
    setChartsSelected((prevData)=> prevData.filter((item)=>item!==id))
  },[]);

  const getOrderedData = (additionalChartObj:any[])=>{
    
    const rows = Math.ceil(additionalChartObj.length/2)
    const ordered:(typeof additionalChartObj[number] | null)[][]=Array.from({length: rows},()=>
      new Array(2).fill(null)
    )
    let itemIndex = 0;

    for(let row = rows -1; row>=0; row--){
      for(let col = 2-1; col>=0;col--){
        if(itemIndex<additionalChartObj.length){
          ordered[row][col] = additionalChartObj[itemIndex++]

        }
      }
    }
    return ordered;
  }

  const handleAddRemoveChart = (e: any) => {
    let charts = e.target.value;
    setChartsSelected(charts)
    if (charts.length > 0) {
      const cSelected = charts.map((chart:any)=>{
        const findingObj = additionalCharts.find((constant) => constant.id === chart);
        return (findingObj)
      });
      setAdditionalChartObj(cSelected)
    }
    else{
      setAdditionalChartObj([])
    }
  }

  const handleYrChange = (newValue:any)=>{
    if (newValue) {
      setDate(newValue);
      /*for current daily it will always be 1-1-year to 12-31-year*/
      const varsYr = {
        stationIds: station?.station_id.toString(),
        zoomLevel: 5,
        startDate: `${moment(newValue).utc().format('YYYY')}-01-01`,
        endDate: `${moment(newValue).utc().format('YYYY')}-12-31`,
        fuelModel: fuelModel,
        page: 0,
        perPage: 300000,
      };
      getTicks(newValue,currStation)
      let failCounterCurrent = 0;
      let wxFailCounterCurrent = 0;
      failCounterCurrent = getCurrent(varsYr, fourMainChartData,failCounterCurrent, moment(newValue).format('YYYY'));
      if(failCounterCurrent>0 && failCounterCurrent<4){
        while(failCounterCurrent>0 && failCounterCurrent<4){
          failCounterCurrent = getCurrent(varsYr, fourMainChartData,failCounterCurrent, moment(newValue).format('YYYY'));
        }
      }
      const varsCurrentWX = {
        stationIds: station?.station_id.toString(),
        zoomLevel: 5,
        startDate: `${moment(newValue).utc().format('YYYY')}-01-01`,
        endDate: `${moment(newValue).utc().format('YYYY')}-12-31`,
        page: 0,
        perPage: 300000,
        sortOrder: 'ASC',
        sortBy: 'summary_date'
      };
      
      wxFailCounterCurrent = getWxCurrentValues(varsCurrentWX, fourMainChartData, wxFailCounterCurrent, moment(newValue).format('YYYY'))
      if(wxFailCounterCurrent>0 && wxFailCounterCurrent<4){
        while(wxFailCounterCurrent>0 && wxFailCounterCurrent<4){
          wxFailCounterCurrent = getWxCurrentValues(varsCurrentWX, wxFailCounterCurrent, fourMainChartData, moment(newValue).format('YYYY'))
          
        }
      }
      setErrorDate(null);
    } else {
      setErrorDate("Year is required");
    }
  };

  const handleFuelModelChange = (e: any)=>{
    setFuelModel(e.target.value);
    const vars = {
      stationId: currStation?.station_id.toString(),
      fuelModel: e.target.value,
      startYear: currStation?.period_record_start? moment(currStation?.period_record_start).utc().format('YYYY'): '2005',
      endYear: currStation?.period_record_stop? moment(currStation?.period_record_stop).utc().format('YYYY'):'2022',
      startMonthDay: '01-01',
      endMonthDay: '12-31',
      startHour: "0",
      endHour: "24",
      dateTimeFormat: 'LocalStationTime',
    }
    const varsPerc = {
      stationId: currStation?.station_id.toString(),
      fuelModel: e.target.value,
      startYear: currStation?.period_record_start? moment(currStation?.period_record_start).utc().format('YYYY'): '2005',
      endYear: '2024',
      startMonthDay: '01-01',
      endMonthDay: '12-31',
      startHour: "0",
      endHour: "24",
      dateTimeFormat: 'LocalStationTime',
    }
    let failCounterCurrent = 0;
    failCounterCurrent = getChartData(mainQuery, vars, varsPerc, failCounterCurrent);
    if (failCounterCurrent > 0 && failCounterCurrent < 4) {
      while (failCounterCurrent > 0 && failCounterCurrent < 4) {
        failCounterCurrent = getChartData(mainQuery, vars, varsPerc, failCounterCurrent);
      }
    }
  }

  return (
    <Box sx={{ position: "relative" }}>
      {/* Period of Record */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          backgroundColor: "white",
          border: "1px solid #ddd",
          borderRadius: "4px",
          padding: "8px 16px",
          margin: "30px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          width: "20%",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", textAlign: "center" }}
        >
          NFDRS Period of Record
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center" }}>
          {currStation?.time_zone_offset && `${moment(currStation?.period_record_start).utc().format('MM-DD-YYYY')} - 12-31-2024`}
        </Typography>
      </Box>
      {/* Filters */}
      <Box className="filter">
        {/* Date Range */}
        <Box>
          <div>
          <label
            htmlFor="yearSelect"
            id="yearSelect"
            className="font-sans-md text-bold"
          >
            Year
          </label>
            <div
              className="year-picker-wrapper"
              style={{ margin: "10px 0" }}
            >
              <DatePicker
                views={['year']}
                value={date}
                onChange={(newValue) => handleYrChange(newValue)}
                slotProps={{
                  textField: {
                    error: !!errorDate,
                    helperText: errorDate,
                    id: "yearSelect",
                    sx: {
                      backgroundColor: "white",
                      borderRadius: "8%",
                    },
                  },
                }}
              />
            </div>
          </div>
        </Box>

        {/* Fuel Model */}
        <div>
          <label
            htmlFor="fuelmodelFireDanger"
            id="fuelmodelFireDanger"
            className="font-sans-md text-bold"
          >
            Fuel Model
          </label>
          <div
            className={`date-range-picker-wrapper`}
            style={{
              margin: "10px 0",
              backgroundColor: "white",
              borderRadius: "8%",
            }}
          >
            <Select
              id="fuelmodelFireDanger"
              name="fuelmodelFireDanger"
              value={fuelModel}
              onChange={(e) => handleFuelModelChange(e)}
              sx={{ minWidth: 200 }}
              inputProps={{
                id: 'fuelmodelFireDanger',
              }}
            >
              <MenuItem value="V">V</MenuItem>
              <MenuItem value="W">W</MenuItem>
              <MenuItem value="X">X</MenuItem>
              <MenuItem value="Y">Y</MenuItem>
              <MenuItem value="Z">Z</MenuItem>
            </Select>
          </div>
        </div>

        {/* Add/Remove Chart */}
        <div>
          <label
            htmlFor="addRemoveChart"
            id="addRemoveChart"
            className="font-sans-md text-bold"
          >
            Add/Remove Chart
          </label>
          <div
            className="date-range-picker-wrapper"
            style={{
              margin: "10px 0",
              backgroundColor: "white",
              borderRadius: "8%",
            }}
          >

            <FormControl sx={{ minWidth: 200 }}>
              <Select
                id="addRemoveChart"
                multiple
                
                value={chartsSelected}
                onChange={(e) => handleAddRemoveChart(e)}
                input={<OutlinedInput />}
                inputProps={{
                  id: 'addRemoveChart',
                }}
                renderValue={(selected) => {
                  if(selected.length>2){
                    return `${selected.length} charts selected`;
                  }
                  else{
                    return(
                      additionalCharts
                      .filter((option) => selected.includes(option.id))
                      .map((option) => option.label)
                      .join(', ')
                    )
                  }
                }
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 4.5 + 8,
                      width: 250,
                    },
                  },
                }}
              >
                {additionalCharts.map((chart) => (
                  <MenuItem key={chart.id} value={chart.id}>
                    <Checkbox checked={chartsSelected.includes(chart.id)} />
                    <ListItemText primary={chart.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </Box>

      {/* Charts */}
      <Box sx ={{backgroundColor: "#f5f5f5"}}>
      {!callsComplete && (

        <div style={{height: '30%'}}>
          <LoadingSpinner/>
        </div>
      )}
      <Suspense fallback={<div>loading</div>}>
        {
          additionalChartObj.length>=1 &&
          <Charts key={additionalChartObj.length} chartsToDisplay={getOrderedData(additionalChartObj)} data={dataUsedForCharts} 
            fuelModel={fuelModel} start={startOfChartX} end={endOfChartX} xTicks={xTicks} selectedYr={yrSelected}
            percentileLines={percentileLines} removeChart={removeChart} station={currStation}
          />
        }
      </Suspense>
      <Suspense fallback={<div>loading</div>}>
        <Charts chartsToDisplay={fourMain} data={dataUsedForCharts} 
          fuelModel={fuelModel} start={startOfChartX} end={endOfChartX} xTicks={xTicks} selectedYr={yrSelected}
          percentileLines={percentileLines} removeChart={removeChart} station={currStation}
        />
      </Suspense>
      </Box>
    </Box>
  );
};

export default ChartsTab;